/* eslint-disable no-unused-expressions */
import React, { useEffect, useState, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import {
	Button,
	Flexgrid,
	FlexgridItem,
	TextInput,
	Form,
} from '@wf-wfria/pioneer-core'
import {
	WFINFOLEASE,
	PLINFOLEASE,
	SBA,
	ONBOARD,
	RICOH,
	DLO,
	COBO,
	EASYCONNECT,
	FORDPRO,
} from '../businessSegmentBrandIds'
import { CustomerContractNumber } from './CustomerContractNumber'
import { InvoiceSerialNumber } from './InvoiceSerialNumber'
import { CustomerContractInvoiceNumberRICOH } from './CustomerContractInvoiceNumberRICOH'
import { SystemMessage } from './SystemMessages'

export function onFocusUserIdInput(selector) {
	const input = document.querySelector(selector)
	if (input) {
		input.focus()
	}
}

export function getRegistrationMessageId(mode, messageId) {
	if (
		mode !== SBA &&
		mode !== ONBOARD &&
		mode !== DLO &&
		mode !== COBO &&
		mode !== EASYCONNECT &&
		messageId.includes('CLDP1017')
	) {
		return `wca-form.new-user-registration.email-validation-message-id`
	}
	return messageId
}

export function RegistrationForm({
	focusedElement,
	mode,
	formatMessage,
	email,
	existingEmail,
	dob,
	customerNumber,
	customerNumberRicoh,
	contractNumber,
	contractNumberRicoh,
	invoiceNumber,
	serialNumber,
	customerOrContract,
	contractOrInvoice,
	invoiceOrSerial,
	registrationMessage,
	customerNumberInputClasses,
	contractNumberInputClasses,
	customerNumberRicohInputClasses,
	contractNumberRicohInputClasses,
	invoiceNumberInputClasses,
	serialNumberInputClasses,
	registrationButtonDisabled,
	emailInputClasses,
	existingEmailInputClasses,
	hideFieldErrors,
	onSetHideFieldErrors,
	onChange,
	onFocusFactory,
	onSelectCustomerOrContract,
	onSelectInvoiceOrSerial,
	onSelectContractOrInvoice,
	onGetEmailConfirmation,
	onTextInputValidator,
}) {
	const formRef = useRef()
	const [emailKey, setEmailKey] = useState(new Date().getTime())
	const [existingEmailKey, setExistingEmailKey] = useState(new Date().getTime())
	const { show, iconKind, messageId } = registrationMessage
	const brandBasedMessageId = getRegistrationMessageId(mode, messageId)
	const showMessage =
		Array.isArray(messageId) &&
		messageId.every((message) => message.includes('CLDP'))
			? true
			: messageId.includes('CLDP')
	const valueMissing = formatMessage({ id: 'required-field.empty.error.label' })
	useEffect(() => {
		if (
			focusedElement !== 'userEmail' &&
			focusedElement !== 'existingUserEmail' &&
			focusedElement !== 'dob' &&
			focusedElement !== 'customerNumber' &&
			focusedElement !== 'contractNumber' &&
			focusedElement !== 'customerNumberRicoh' &&
			focusedElement !== 'contractNumberRicoh' &&
			focusedElement !== 'invoiceNumber' &&
			focusedElement !== 'serialNumber'
		) {
			if (formRef?.current?.resetField) {
				formRef?.current?.resetField('userEmail')
			}
			if (
				mode === RICOH ||
				mode === PLINFOLEASE ||
				mode === WFINFOLEASE ||
				mode === FORDPRO
			) {
				if (formRef?.current?.resetField) {
					formRef?.current?.resetField('existingUserEmail')
				}
			}
			const newKey = new Date().getTime()
			setEmailKey(newKey)
			setExistingEmailKey(newKey)
		}
	}, [focusedElement, setExistingEmailKey, setEmailKey, mode])

	useEffect(() => {
		if (hideFieldErrors.email) {
			formRef.current.resetField('userEmail')
			const resetHideFieldErrors = { email: false }
			onSetHideFieldErrors(resetHideFieldErrors)
		}
		if (hideFieldErrors.existingEmail) {
			formRef.current.resetField('existingUserEmail')
			const resetHideFieldErrors = { existingEmail: false }
			onSetHideFieldErrors(resetHideFieldErrors)
		}
	}, [onSetHideFieldErrors, hideFieldErrors])

	useEffect(() => {
		if (show) {
			onFocusUserIdInput('.RegistrationForm #email')
		}
	}, [show])

	function onContinueRegistrationClick() {
		const canContinue = !registrationButtonDisabled
		if (!canContinue) {
			return null
		}
		onFocusUserIdInput('#userEmail')
		if (formRef && formRef.current) {
			formRef.current.validateField('userEmail')
		}
		return onGetEmailConfirmation()
	}

	switch (mode) {
		case RICOH:
			return (
				<Form action="#" ref={formRef} onSubmit={onContinueRegistrationClick}>
					<Flexgrid rowGap={1} direction="column">
						<FlexgridItem span={12}>
							<SystemMessage
								className="RegistrationMessage"
								show={showMessage}
								iconKind={iconKind}
								messageId={brandBasedMessageId}
							/>
						</FlexgridItem>
						<Flexgrid rowGap={2}>
							<FlexgridItem span={12}>
								<TextInput
									key={emailKey}
									aria-label={formatMessage({
										id: 'wca-form.new-user-registration.email.label',
									})}
									label={formatMessage({
										id: 'wca-form.new-user-registration.email.label',
									})}
									aria-required={true}
									required={true}
									labelHidden={true}
									id="userEmail"
									name="userEmail"
									type="text"
									size="25"
									value={email.value}
									onValueChange={onChange}
									onFocus={onFocusFactory('WcaRegister')}
									customValidators={[
										(value) => onTextInputValidator('emailInput', value),
									]}
									nativeValidationMessages={{
										valueMissing,
									}}
								/>
							</FlexgridItem>
							<FlexgridItem span={12}>
								<TextInput
									key={existingEmailKey}
									aria-label={formatMessage({
										id: 'wca-form.new-user-registration.existingEmail.label',
									})}
									label={formatMessage({
										id: 'wca-form.new-user-registration.existingEmail.label',
									})}
									aria-required={true}
									required={true}
									labelHidden={true}
									id="existingUserEmail"
									name="existingUserEmail"
									type="text"
									size="25"
									value={existingEmail.value}
									onValueChange={onChange}
									onFocus={onFocusFactory('WcaRegister')}
									customValidators={[
										(value) =>
											onTextInputValidator('existingEmailInput', value),
									]}
									nativeValidationMessages={{
										valueMissing,
									}}
								/>
							</FlexgridItem>
						</Flexgrid>
						<CustomerContractInvoiceNumberRICOH
							focusedElement={focusedElement}
							mode={mode}
							formatMessage={formatMessage}
							customerNumberRicoh={customerNumberRicoh}
							contractNumberRicoh={contractNumberRicoh}
							invoiceNumber={invoiceNumber}
							contractOrInvoice={contractOrInvoice}
							customerNumberInputClasses={customerNumberInputClasses}
							contractNumberInputClasses={contractNumberInputClasses}
							customerNumberRicohInputClasses={customerNumberRicohInputClasses}
							contractNumberRicohInputClasses={contractNumberRicohInputClasses}
							invoiceNumberInputClasses={invoiceNumberInputClasses}
							onChange={onChange}
							onFocusFactory={onFocusFactory}
							onSelectContractOrInvoice={onSelectContractOrInvoice}
							emailInputClasses={emailInputClasses}
							onTextInputValidator={onTextInputValidator}
						/>
						<FlexgridItem span={12} style={{ marginTop: '16px' }}>
							<Button
								type="submit"
								className="RegistrationButton"
								kind="primary"
								responsive
							>
								<FormattedMessage id="wca-form.new-user-registration.button.continue.label" />
							</Button>
						</FlexgridItem>
					</Flexgrid>
				</Form>
			)
		case PLINFOLEASE:
		case WFINFOLEASE:
		case FORDPRO:
			return (
				<Form action="#" ref={formRef} onSubmit={onContinueRegistrationClick}>
					<Flexgrid direction="column">
						<FlexgridItem span={{ xxs: 12, sm: 12, md: 12 }}>
							<SystemMessage
								className="RegistrationMessage"
								show={showMessage}
								iconKind={iconKind}
								messageId={brandBasedMessageId}
							/>
						</FlexgridItem>
						<Flexgrid rowGap={2}>
							<FlexgridItem span={12}>
								<TextInput
									key={emailKey}
									aria-label={formatMessage({
										id: 'wca-form.new-user-registration.email.label',
									})}
									label={formatMessage({
										id: 'wca-form.new-user-registration.email.label',
									})}
									labelHidden={true}
									aria-required={true}
									required={true}
									id="userEmail"
									name="userEmail"
									type="text"
									size="25"
									value={email.value}
									onValueChange={onChange}
									onFocus={onFocusFactory('WcaRegister')}
									customValidators={[
										(value) => onTextInputValidator('emailInput', value),
									]}
								/>
							</FlexgridItem>
							<FlexgridItem span={12}>
								<TextInput
									key={existingEmailKey}
									aria-label={formatMessage({
										id: 'wca-form.new-user-registration.existingEmail.label',
									})}
									label={formatMessage({
										id: 'wca-form.new-user-registration.existingEmail.label',
									})}
									aria-required={true}
									required={true}
									labelHidden={true}
									id="existingUserEmail"
									name="existingUserEmail"
									type="text"
									size="25"
									value={existingEmail.value}
									onValueChange={onChange}
									onFocus={onFocusFactory('WcaRegister')}
									customValidators={[
										(value) =>
											onTextInputValidator('existingEmailInput', value),
									]}
									nativeValidationMessages={{
										valueMissing,
									}}
								/>
							</FlexgridItem>
						</Flexgrid>
						<FlexgridItem>
							<Flexgrid
								direction="column"
								rowGap={1}
								style={{ marginTop: '16px' }}
							>
								<FlexgridItem span="auto">
									<CustomerContractNumber
										focusedElement={focusedElement}
										mode={mode}
										formatMessage={formatMessage}
										customerNumber={customerNumber}
										contractNumber={contractNumber}
										customerOrContract={customerOrContract}
										customerNumberInputClasses={customerNumberInputClasses}
										contractNumberInputClasses={contractNumberInputClasses}
										onChange={onChange}
										onFocusFactory={onFocusFactory}
										onSelectCustomerOrContract={onSelectCustomerOrContract}
										onTextInputValidator={onTextInputValidator}
									/>
								</FlexgridItem>
								<FlexgridItem span="auto">
									<InvoiceSerialNumber
										focusedElement={focusedElement}
										mode={mode}
										formatMessage={formatMessage}
										invoiceNumber={invoiceNumber}
										serialNumber={serialNumber}
										invoiceOrSerial={invoiceOrSerial}
										invoiceNumberInputClasses={invoiceNumberInputClasses}
										serialNumberInputClasses={serialNumberInputClasses}
										onChange={onChange}
										onFocusFactory={onFocusFactory}
										onSelectInvoiceOrSerial={onSelectInvoiceOrSerial}
										emailInputClasses={emailInputClasses}
										onTextInputValidator={onTextInputValidator}
									/>
								</FlexgridItem>
							</Flexgrid>
						</FlexgridItem>
						<FlexgridItem style={{ marginTop: '16px' }}>
							<Button
								type="submit"
								className="RegistrationButton"
								kind="primary"
								responsive
							>
								<FormattedMessage id="wca-form.new-user-registration.button.continue.label" />
							</Button>
						</FlexgridItem>
					</Flexgrid>
				</Form>
			)

		default:
			return (
				<Form action="#" ref={formRef}>
					<div className="RegistrationForm form-group">
						<div className="wf-row">
							<div className="wf-col--12">
								<SystemMessage
									className="RegistrationMessage"
									show={show}
									iconKind={iconKind}
									messageId={brandBasedMessageId}
								/>
							</div>
						</div>
						<div className="wf-row RegistrationForm">
							<TextInput
								key={emailKey}
								aria-label={formatMessage({
									id: 'wca-form.new-user-registration.email.label',
								})}
								aria-required={true}
								required={false}
								id="userEmail"
								name="userEmail"
								type="text"
								size="25"
								value={email.value}
								onChange={onChange}
								onFocus={onFocusFactory('WcaRegister')}
								className={emailInputClasses}
								customValidators={[
									(value) => onTextInputValidator('emailInput', value),
								]}
								labelClassName="wf-col wf-col--12 wf-col--mob-only-12"
								fieldClassName="wf-col wf-col--12 wf-col--mob-only-12"
								nativeValidationMessages={{
									valueMissing,
								}}
							/>
						</div>
						<div className="wf-row">
							<div className="wf-col--mob-only-3 wf-col--tab-12 wf-col--desk-8">
								<Button
									className="RegistrationButton"
									kind="primary"
									responsive
									onClick={onContinueRegistrationClick}
								>
									<FormattedMessage id="wca-form.new-user-registration.button.continue.label" />
								</Button>
							</div>
						</div>
					</div>
				</Form>
			)
	}
}
